/**
 * The edits made to one lesson in one sitting, and the way back — GEN-43.
 *
 * The console autosaves on blur, so an undo is not a local revert but the previous value
 * written back through the same API. That is why a step carries both writes rather than a
 * diff: replaying one is the same operation in either direction, so nothing here has to
 * invert an edit it has already sent.
 */

/** One committed edit, and the write that puts it back. */
export interface Step<Write> {
  /** The field in the reviewer's words: "Title", "Scene 2 Prompt". */
  label: string;
  before: Write;
  after: Write;
}

/**
 * `undone` counts back from the end: an undone step is still in the list, waiting to be
 * redone. Dropping it on undo would make redo impossible, and is the shape this is
 * deliberately not.
 */
export interface History<Write> {
  steps: readonly Step<Write>[];
  undone: number;
}

export const NOTHING_DONE: History<never> = { steps: [], undone: 0 };

export type HistoryEvent<Write> =
  { kind: 'did'; step: Step<Write> } | { kind: 'undo' } | { kind: 'redo' };

/** The step an undo would step back, or `undefined` when there is nothing behind. */
export function undoable<Write>(
  history: History<Write>,
): Step<Write> | undefined {
  return history.steps[history.steps.length - history.undone - 1];
}

/** The step a redo would put back, or `undefined` when nothing has been undone. */
export function redoable<Write>(
  history: History<Write>,
): Step<Write> | undefined {
  return history.steps[history.steps.length - history.undone];
}

/**
 * A new edit after an undo drops what was undone.
 *
 * Keeping the redo would offer a button that reapplies a value the reviewer has since
 * typed over, and no part of the screen would predict that.
 */
export function reduceHistory<Write>(
  history: History<Write>,
  event: HistoryEvent<Write>,
): History<Write> {
  if (event.kind === 'did')
    return {
      steps: [
        ...history.steps.slice(0, history.steps.length - history.undone),
        event.step,
      ],
      undone: 0,
    };

  if (event.kind === 'undo')
    return undoable(history) === undefined
      ? history
      : { ...history, undone: history.undone + 1 };

  return redoable(history) === undefined
    ? history
    : { ...history, undone: history.undone - 1 };
}

/**
 * Several edits as one step — ADMIN-27's Apply all, so one Undo takes the batch back.
 *
 * Undone newest first: two edits to the same field each hold the value before the batch,
 * and in that order the one written last is the original.
 */
export function together<Write>(
  steps: readonly Step<Write>[],
): Step<readonly Write[]> {
  const [only, ...rest] = steps;

  return {
    label:
      only !== undefined && rest.length === 0
        ? only.label
        : `${String(steps.length)} edits`,
    before: steps.map((one) => one.before).reverse(),
    after: steps.map((one) => one.after),
  };
}
